import excel from "exceljs";
import {
  fetchPartSKPBySupplier,
  fetchExchangeRateMaster,
  fetchFirstTierSuppliers,
  fetchLegacyPartActive,
} from "./cachedQueries";
import { getCellStr, getCellNum } from "./sheetHelpers";
import type { exchangeRateMaster } from "@/api/breakdownExchangeRateService";

export type OtherProcessCostItem = { name: string; cost: number };

export type ImportedExchangeRatePartRow = {
  part_id: number;
  part_number: string;
  ac: string;
  rt: string;
  exchange_rate_supplier_id: number;
  currency_id: number;
  currency_code: string;
  price: number;
  oem_other_process: OtherProcessCostItem[];
  spare_other_process: OtherProcessCostItem[];
};

export type ExchangeRateImportError = {
  rowNumber: number;
  partNo: string;
  ac: string;
  rt: string;
  message: string;
};

export type ExchangeRateImportWarning = {
  rowNumber: number;
  partNo: string;
  message: string;
};

export type ParsedExchangeRateImportResult = {
  supplierCode: string;
  rows: ImportedExchangeRatePartRow[];
  errors: ExchangeRateImportError[];
  warnings: ExchangeRateImportWarning[];
};

export const buildExchangeRateLabel = (
  m: { exchange_rate_supplier_name: string; currency_name: string },
  supplierAbbr: string,
): string =>
  `${m.exchange_rate_supplier_name}_None_None_${supplierAbbr}_${m.currency_name}`;

const OEM_MARKER = "OEM Process";
const SPARE_MARKER = "Spare Process";
const HEADER_ROW = 5;
const FIRST_DATA_ROW = 6;
const PROCESS_COL_SEARCH_START = 11;

/** header cell is "OEM Process\n(THB/pcs)" — strip the newline, compare only the first line */
const normalizeHeaderText = (v: string): string => v.split("\n")[0].trim();

export const parseExchangeRatePartFromExcel = async (
  file: File,
  supplierCode: string,
  activeFilter: 0 | 1 = 1,
): Promise<ParsedExchangeRateImportResult> => {
  const errors: ExchangeRateImportError[] = [];
  const warnings: ExchangeRateImportWarning[] = [];
  const rows: ImportedExchangeRatePartRow[] = [];

  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const sheet = workbook.getWorksheet("CBD");
  if (!sheet) throw new Error("Sheet 'CBD' not found");

  const [partSKPList, exchangeRateMaster, firstTierSuppliers, legacyPartActive] = await Promise.all([
    fetchPartSKPBySupplier(supplierCode),
    fetchExchangeRateMaster(),
    fetchFirstTierSuppliers(),
    fetchLegacyPartActive(supplierCode),
  ]);

  // ไฟล์ CBD ของ ExRate ไม่มีคอลัมน์ is_active — เทียบกับ legacy Parts เอาเอง
  const legacyActiveMap = new Map<string, boolean>(
    legacyPartActive.map((p) => [`${p.part_no}|${p.ac_code}|${p.rt_code}`, p.is_active]),
  );

  const supplierAbbr =
    firstTierSuppliers.find((s) => s.supplier_code === supplierCode)?.supplier_abbr ?? "";

  const labelMap = new Map<string, exchangeRateMaster>(
    exchangeRateMaster.map((m) => [buildExchangeRateLabel(m, supplierAbbr), m]),
  );

  // ── หา marker column (OEM Process / Spare Process) แบบ dynamic ──
  let oemMarkerCol = -1;
  let spareMarkerCol = -1;
  for (let c = PROCESS_COL_SEARCH_START; c <= sheet.columnCount; c++) {
    const h = normalizeHeaderText(getCellStr(sheet, HEADER_ROW, c));
    if (h === OEM_MARKER && oemMarkerCol === -1) oemMarkerCol = c;
    if (h === SPARE_MARKER) spareMarkerCol = c;
  }
  if (oemMarkerCol === -1 || spareMarkerCol === -1) {
    throw new Error(
      "โครงสร้างไฟล์ไม่ถูกต้อง: ไม่พบคอลัมน์ \"OEM Process\" หรือ \"Spare Process\" ในแถว header",
    );
  }

  // ── Detail column ระหว่าง/หลัง marker คือชื่อ process จริง — เก็บชื่อไว้เฉยๆ
  // ไม่ต้อง match กับระบบล่วงหน้าอีกต่อไป (backend auto-create process master ที่ยังไม่มีให้เอง)
  const oemProcessNameByCol = new Map<number, string>();
  for (let c = oemMarkerCol + 1; c < spareMarkerCol; c++) {
    oemProcessNameByCol.set(c, getCellStr(sheet, HEADER_ROW, c));
  }

  const spareProcessNameByCol = new Map<number, string>();
  for (let c = spareMarkerCol + 1; c <= sheet.columnCount; c++) {
    spareProcessNameByCol.set(c, getCellStr(sheet, HEADER_ROW, c));
  }

  // ── วนแถวข้อมูลจนกว่าคอลัมน์ A จะว่าง ──
  for (let r = FIRST_DATA_ROW; ; r++) {
    const partNo = getCellStr(sheet, r, 1);
    if (!partNo) break;

    const ac = getCellStr(sheet, r, 3);
    const rt = getCellStr(sheet, r, 4);

    // กรอง active/inactive ก่อนเช็ค error อื่นใดๆ — แถวนอกชุดของ panel นี้ต้องไม่ไปบล็อก
    // supplier ทั้งก้อน (commit เป็น atomic ต่อ supplier) ไม่เจอใน legacy Parts เลย = ถือว่า active
    const legacyIsActive = legacyActiveMap.get(`${partNo}|${ac}|${rt}`) ?? true;
    if ((legacyIsActive ? 1 : 0) !== activeFilter) {
      continue;
    }
    if (!legacyActiveMap.has(`${partNo}|${ac}|${rt}`)) {
      warnings.push({
        rowNumber: r,
        partNo,
        message: `ไม่พบสถานะ active ในระบบเก่า (แถวที่ ${r}) — ถือเป็น active`,
      });
    }

    const materialCode = getCellStr(sheet, r, 7);
    const price = getCellNum(sheet, r, 8);

    const matchedPart = partSKPList.find(
      (p) => p.part_number === partNo && p.ac === ac && p.rt === rt,
    );
    const matchedCurrency = labelMap.get(materialCode);

    if (!matchedPart) {
      errors.push({
        rowNumber: r,
        partNo,
        ac,
        rt,
        message: `ไม่พบ Part ในระบบ (แถวที่ ${r}): Part No="${partNo}" A/C="${ac}" R/T="${rt}"`,
      });
    }
    if (!matchedCurrency) {
      errors.push({
        rowNumber: r,
        partNo,
        ac,
        rt,
        message: `ไม่พบ Material Code ในระบบ (แถวที่ ${r}): "${materialCode}"`,
      });
    }

    if (matchedPart && matchedCurrency) {
      const oem_other_process: OtherProcessCostItem[] = [...oemProcessNameByCol].map(
        ([col, name]) => ({ name, cost: getCellNum(sheet, r, col) }),
      );
      const spare_other_process: OtherProcessCostItem[] = [...spareProcessNameByCol].map(
        ([col, name]) => ({ name, cost: getCellNum(sheet, r, col) }),
      );

      rows.push({
        part_id: matchedPart.part_id,
        part_number: partNo,
        ac,
        rt,
        exchange_rate_supplier_id: matchedCurrency.exchange_rate_supplier_id,
        currency_id: matchedCurrency.currency_id,
        currency_code: matchedCurrency.currency_name,
        price,
        oem_other_process,
        spare_other_process,
      });
    }
  }

  return { supplierCode, rows, errors, warnings };
};
